import TranslationService from "../../services/TranslationService";
import Vue from "vue";
import { mapState } from "vuex";

const ModalService = require("../../services/ModalService");
const NotificationService = require("../../services/NotificationService");

const TELEPHONE_OPTION_TYPE_ID = 4;

// Maps address option typeIds to the flat field names the IO address endpoint
// expects on write. Mirrors AddressSelect.addressOptionTypeFieldMap.
const ADDRESS_OPTION_TYPE_FIELD_MAP = {
    1: "vatNumber",
    4: "telephone",
    5: "email",
    6: "postNumber",
    9: "birthday",
    11: "title",
    12: "contactPerson"
};

export default Vue.component("phone-required-hint", {

    props:
    {
        template:
        {
            type: String,
            default: "#vue-phone-required-hint"
        },
        requiredProfileIds:
        {
            type: Array,
            default: () => []
        }
    },

    data()
    {
        return {
            phoneModal: {},
            phoneInput: "",
            isPending: false
        };
    },

    computed:
    {
        ...mapState({
            shippingProfileId: state => state.checkout.shipping.shippingProfileId,
            deliveryAddress: state => state.address.deliveryAddress,
            billingAddress: state => state.address.billingAddress,
            deliveryAddressId: state => state.address.deliveryAddressId
        }),

        cleanProfileIds()
        {
            return this.requiredProfileIds
                .map(id => parseInt(id, 10))
                .filter(id => !isNaN(id));
        },

        isPhoneRequired()
        {
            return this.cleanProfileIds.indexOf(parseInt(this.shippingProfileId, 10)) !== -1;
        },

        // A number on EITHER address is sufficient (rule: one is enough). Show the
        // delivery number if present, otherwise the billing number — so the widget
        // says "change" instead of "required" when the customer is already reachable.
        // Saving still always targets the delivery address (see getTargetAddress).
        currentPhone()
        {
            return this.getTelephone(this.deliveryAddress) || this.getTelephone(this.billingAddress);
        },

        // Always show the widget when a phone-required shipping method is selected and
        // an address exists — also when a number is already stored, so it stays visible
        // and editable rather than disappearing.
        showWidget()
        {
            return this.isPhoneRequired && this.getTargetAddress() !== null;
        },

        // In the ShopBuilder editor the real shipping/address data is dummy data,
        // so the live conditions never match. Force the hint visible there so the
        // merchant can see and style the widget.
        isShopBuilder()
        {
            return typeof App !== "undefined" && !!App.isShopBuilder;
        }
    },

    methods:
    {
        /**
         * Address the phone number should be written to:
         * delivery address if a separate one is selected, otherwise billing.
         */
        getTargetAddress()
        {
            if (this.deliveryAddress && this.deliveryAddressId > 0)
            {
                return { address: this.deliveryAddress, type: "2" };
            }

            if (this.billingAddress)
            {
                return { address: this.billingAddress, type: "1" };
            }

            return null;
        },

        openModal()
        {
            this.phoneInput = this.currentPhone;

            // Look up the modal fresh on open: it lives inside a v-if, so a reference
            // cached in mounted() would point at a stale/detached node after the box
            // re-renders (e.g. when the selected shipping method changes).
            this.phoneModal = ModalService.findModal(this.$refs.phoneRequiredModal);
            this.phoneModal.show();
        },

        closeModal()
        {
            if (this.phoneModal && this.phoneModal.hide)
            {
                this.phoneModal.hide();
            }

            this.isPending = false;
        },

        savePhoneNumber()
        {
            const target = this.getTargetAddress();

            if (!target || this.isPending)
            {
                return;
            }

            const phone = this.phoneInput ? this.phoneInput.trim() : "";

            if (phone.length <= 0)
            {
                return;
            }

            this.isPending = true;

            // The IO address endpoint reads option-type values (telephone, email, ...)
            // from FLAT fields on write and maps them back into the options array
            // internally. Mirror what the plenty address form submits: keep the existing
            // options, expose every option value as a flat field, and set the telephone
            // flat field. Writing into options[] directly does NOT persist.
            const address = this.buildEditableAddress(target.address);

            address.telephone = phone;

            this.$store.dispatch("updateAddress", { address: address, addressType: target.type })
                .then(() =>
                {
                    this.closeModal();
                })
                .catch(() =>
                {
                    this.isPending = false;
                    NotificationService.error(
                        TranslationService.translate("Ceres::Template.errorActionIsNotExecuted")
                    );
                });
        },

        // Deep-clone an address and expose each option value as a flat field,
        // mirroring AddressSelect.getAddressToEdit so the payload matches what the
        // plenty address form submits on update.
        buildEditableAddress(sourceAddress)
        {
            const address = JSON.parse(JSON.stringify(sourceAddress));

            if (Array.isArray(address.options))
            {
                for (const option of address.options)
                {
                    const fieldName = ADDRESS_OPTION_TYPE_FIELD_MAP[option.typeId];

                    if (fieldName)
                    {
                        address[fieldName] = option.value || null;
                    }
                }
            }

            return address;
        },

        getTelephone(address)
        {
            if (!address)
            {
                return "";
            }

            if (Array.isArray(address.options))
            {
                const option = address.options.find(opt => opt.typeId === TELEPHONE_OPTION_TYPE_ID);

                if (option && option.value)
                {
                    return "" + option.value;
                }
            }

            // After saving via this widget the store address carries telephone as a
            // flat field (it lands in options only on the next full load), so fall back to it.
            if (address.telephone)
            {
                return "" + address.telephone;
            }

            return "";
        }
    }
});
